import { isExperienceProduct, isGiftOfferProduct, isPackageProduct, isPartnerPackageProduct } from "common";
import { useEffect } from "react";
import { Product, UserDetailsRequirement } from "types";
import { useCustomerDetailsStore } from "../store/customerDetailsStore";
import { useSeller } from "@/hooks/useAccount";
import { getCustomerNamePreference, isCustomerNameRequired } from "@/models/Experience";
import { getStrictestRequirement } from "@/models/GiftPreference";
import { getProductIdsInPackage } from "@/models/Package";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

export const useSetCustomerNameRequired = (product: Product) => {
    const giftPreference = useSeller().preferences.gift;

    useEffect(() => {
        const isExperience = isExperienceProduct(product);
        const { showCustomerFields } = useCustomerDetailsStore.getState();

        // The organizer section is shared by every item, so a cart mixing gifts and other products uses its strictest rule.
        const productsInCart = useCreatePurchaseStore.getState().getProductsInPurchase();
        if (productsInCart.length > 1 && productsInCart.some((cartProduct) => isGiftOfferProduct(cartProduct))) {
            const requirement = getStrictestRequirement(
                productsInCart.map((cartProduct) => getCustomerNamePreference(cartProduct, giftPreference)),
            );
            useCustomerDetailsStore.setState({
                showCustomerFields: requirement !== UserDetailsRequirement.NOT_COLLECTED,
                nameRequired: isCustomerNameRequired(requirement),
            });

            return;
        }
        if (isExperience) {
            const namePreference = getCustomerNamePreference(product);

            if (!showCustomerFields) {
                useCustomerDetailsStore.setState({
                    showCustomerFields: namePreference !== UserDetailsRequirement.NOT_COLLECTED,
                });
            }

            const isNameRequired = useCustomerDetailsStore.getState().nameRequired;
            if (isNameRequired && namePreference !== UserDetailsRequirement.REQUIRED) {
                useCustomerDetailsStore.setState({
                    nameRequired: isCustomerNameRequired(namePreference),
                });

                return;
            }
        }

        const isVariableDemographicsPackage = useCreatePurchaseStore.getState().isVariableDemographicsPackage();
        const isPackage = isPackageProduct(product) || isPartnerPackageProduct(product);

        if (isPackage && isVariableDemographicsPackage) {
            const pkgExperiences = useCreatePurchaseStore.getState().getProductsInPurchase();

            const experienceIds = getProductIdsInPackage(product);
            const packageExperiences = pkgExperiences.filter((experience) => experienceIds?.includes(experience.id));

            const isAllExperienceCustomerNotCollected = packageExperiences.every(
                (experience) => experience.customerNamePreference === UserDetailsRequirement.NOT_COLLECTED,
            );

            if (!showCustomerFields) {
                useCustomerDetailsStore.setState({
                    showCustomerFields: !isAllExperienceCustomerNotCollected,
                });
            }

            const isNameRequired = packageExperiences.some((experience) =>
                isCustomerNameRequired(experience.customerNamePreference),
            );

            useCustomerDetailsStore.setState({ nameRequired: isNameRequired });

            return;
        }

        // Handle non-package, non-experience products as fallback
        const namePreference = getCustomerNamePreference(product, giftPreference);

        if (!showCustomerFields) {
            useCustomerDetailsStore.setState({
                showCustomerFields: namePreference !== UserDetailsRequirement.NOT_COLLECTED,
            });
        }

        useCustomerDetailsStore.setState({
            nameRequired: isCustomerNameRequired(namePreference),
        });
    }, [product, giftPreference]);
};
